import { hasIntro } from '../utils/hasIntro'

import type { Challenge } from '@/features/journeys'
import {
  InitialStep,
  IntroStep,
  SlideStep,
  ChallengeStep,
  SummaryStep,
  STEP_KIND,
  Step
} from '@/shared/lib/steps-flow/types'

import { groupChallengesForStoryMerge } from './groupChallengesForStoryMerge'

const slidesOf = (ch: Challenge) => {
  // Only `slide` challenges use challenge_options as sequential slide cards.
  // Other kinds (question, this_or_that, mosaic, stories, etc.) store
  // alternatives/associations in challenge_options and handle them internally.
  if (ch.kind !== 'slide') return []

  const challengeOptions = (ch as Challenge & { challenge_options?: unknown[] })
    .challenge_options
  if (Array.isArray(challengeOptions) && challengeOptions.length > 0) {
    return challengeOptions
  }

  return Array.isArray(ch.meta?.slides) ? ch.meta!.slides! : []
}

const KIND_ORDER: Record<STEP_KIND, number> = {
  [STEP_KIND.INITIAL]: 0,
  [STEP_KIND.INTRO]: 1,
  [STEP_KIND.CHALLENGE]: 3,
  [STEP_KIND.SUMMARY]: 4
}

type GetStepsOptions = {
  includeInitial?: boolean
  includeFinishSummary?: boolean
  filterChallenge?: (ch: Challenge) => boolean
}

export function getSteps(
  challenges: Challenge[] | null | undefined,
  opts: GetStepsOptions = {}
): Step[] {
  const {
    includeInitial = true,
    includeFinishSummary = true,
    filterChallenge = () => true
  } = opts

  if (!challenges?.length) return []

  const actives = challenges
    .filter(filterChallenge)
    .map(ch => ({ ...ch, position: Number(ch.position) || 0 }))

  const groups = groupChallengesForStoryMerge(actives)

  const steps: Step[] = []

  if (includeInitial) {
    steps.push({
      id: 'step-initial',
      kind: STEP_KIND.INITIAL,
      isInitial: true,
      position: -Infinity
    } as InitialStep)
  }

  for (const group of groups) {
    if (group.kind === 'merged_stories') {
      const run = group.challenges
      const ids = run.map(c => c.id)
      const mergedStepId = `step-merged-stories-${ids.join('-')}`
      const position = Math.min(...run.map(c => c.position))
      const firstWithIntro = run.find(ch => hasIntro(ch))
      if (firstWithIntro) {
        steps.push({
          id: `${mergedStepId}-intro`,
          kind: STEP_KIND.INTRO,
          isIntro: true,
          challengeId: firstWithIntro.id,
          text: firstWithIntro.intro ?? null,
          position
        } as IntroStep)
      }

      const maxTimeLimit = Math.max(
        0,
        ...run.map(c => Number(c.time_limit) || 0)
      )
      const maxMinAdvance = Math.max(
        0,
        ...run.map(c => c.minimum_time_to_advance_in_seconds ?? 0)
      )

      steps.push({
        id: mergedStepId,
        kind: STEP_KIND.CHALLENGE,
        challengeId: run[0].id,
        position,
        challengeKind: 'stories',
        time_limit: maxTimeLimit > 0 ? maxTimeLimit : null,
        minimumTimeToAdvanceInSeconds:
          maxMinAdvance > 0 ? maxMinAdvance : null,
        mergedStoryChallengeIds: ids
      } as ChallengeStep)

      continue
    }

    const ch = group.challenge
    const pos = ch.position

    if (hasIntro(ch)) {
      steps.push({
        id: `step-${ch.id}-intro`,
        kind: STEP_KIND.INTRO,
        isIntro: true,
        challengeId: ch.id,
        text: ch.intro ?? null,
        position: pos
      } as IntroStep)
    }

    const slides = slidesOf(ch)
    if (slides.length > 0) {
      slides.forEach((_, i) => {
        steps.push({
          id: `step-${ch.id}-slide-${i + 1}`,
          kind: STEP_KIND.CHALLENGE,
          challengeId: ch.id,
          position: pos,
          challengeKind: ch.kind,
          minimumTimeToAdvanceInSeconds:
            ch.minimum_time_to_advance_in_seconds ?? null,
          isSlide: true,
          slideIndex: i,
          isFirstSlide: i === 0,
          isLastSlide: i === slides.length - 1
        } as SlideStep)
      })
    } else {
      steps.push({
        id: `step-${ch.id}-main`,
        kind: STEP_KIND.CHALLENGE,
        challengeId: ch.id,
        position: pos,
        challengeKind: ch.kind,
        time_limit: ch.time_limit,
        minimumTimeToAdvanceInSeconds:
          ch.minimum_time_to_advance_in_seconds ?? null
      } as ChallengeStep)
    }
  }

  if (includeFinishSummary) {
    steps.push({
      id: 'step-summary-finish',
      kind: STEP_KIND.SUMMARY,
      position: +Infinity,
      result: 'success'
    } as SummaryStep)
  }

  steps.sort((a, b) => {
    if (a.position !== b.position) return a.position - b.position
    const ko = KIND_ORDER[a.kind] - KIND_ORDER[b.kind]
    if (ko !== 0) return ko
    const ca = a.challengeId ?? Number.MAX_SAFE_INTEGER
    const cb = b.challengeId ?? Number.MAX_SAFE_INTEGER
    if (ca !== cb) return ca - cb
    return String(a.id).localeCompare(String(b.id))
  })

  return steps
}
